import { html } from 'lit';

// Monochrome UI icons live here as inline SVG so they paint in currentColor
// and follow whatever text color a block is themed with. Brand and
// illustrative art (the logo, 360 badge, file-format and CTA icons) keeps its
// own colors and stays an <img> against /static/icons/SVG.
const ICONS = {
  arrow: classes => html`
    <svg
      class=${classes}
      width="7"
      height="12"
      viewBox="0 0 7 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <polyline
        points="1 1 6 6 1 11"
        stroke="currentColor"
        stroke-width="1.5"
      />
    </svg>
  `,
  'arrow-thin': classes => html`
    <svg
      class=${classes}
      width="4"
      height="8"
      viewBox="0 0 4 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <polyline points="0 0 4 4 0 8" stroke="currentColor" />
    </svg>
  `,
  download: classes => html`
    <svg
      class=${classes}
      width="10"
      height="10"
      viewBox="0 0 10 10"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        fill-rule="nonzero"
        d="M4.44444444,0 L4.44444444,5.86805556 L1.51041667,2.93402778 L0.711805556,3.73263889 L4.60069444,7.62152778 L5,8.00347222 L5.39930556,7.62152778 L9.28819444,3.73263889 L8.48958333,2.93402778 L5.55555556,5.86805556 L5.55555556,0 L4.44444444,0 Z M0,8.88888889 L0,10 L10,10 L10,8.88888889 L0,8.88888889 Z"
      />
    </svg>
  `,
  'download-large': classes => html`
    <svg
      class=${classes}
      width="12"
      height="16"
      viewBox="0 0 12 16"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        fill-rule="nonzero"
        d="M5.33333333,0 L5.33333333,11.0416667 L1.8125,7.52083333 L0.854166667,8.47916667 L5.52083333,13.1458333 L6,13.6041667 L6.47916667,13.1458333 L11.1458333,8.47916667 L10.1875,7.52083333 L6.66666667,11.0416667 L6.66666667,0 L5.33333333,0 Z M0,14.6666667 L0,16 L12,16 L12,14.6666667 L0,14.6666667 Z"
      />
    </svg>
  `,
  // Redraws the legacy material-properties "i" toggletip glyph so it centers
  // the same regardless of font metrics.
  info: classes => html`
    <svg
      class=${classes}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        class="ring"
        cx="8"
        cy="8"
        r="7"
        stroke="currentColor"
        stroke-width="2"
      />
      <rect x="7.2" y="3.6" width="1.6" height="1.6" fill="currentColor" />
      <rect x="7.2" y="6.2" width="1.6" height="6.2" fill="currentColor" />
    </svg>
  `,
  plus: classes => html`
    <svg
      class=${classes}
      width="10"
      height="10"
      viewBox="0 0 10 10"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <polygon
        fill="currentColor"
        fill-rule="nonzero"
        points="5.76 9.772 5.76 6.208 9.054 6.208 9.054 4.516 5.76 4.516 5.76 0.952 3.942 0.952 3.942 4.516 0.666 4.516 0.666 6.208 3.942 6.208 3.942 9.772"
      />
    </svg>
  `,
  minus: classes => html`
    <svg
      class=${classes}
      width="9"
      height="3"
      viewBox="0 0 9 3"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <polygon
        fill="currentColor"
        fill-rule="nonzero"
        points="8.748 2.18 8.748 0.668 0.81 0.668 0.81 2.18"
      />
    </svg>
  `,
  swap: classes => html`
    <svg
      class=${classes}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        fill-rule="nonzero"
        d="M11.2,1.6 L15.2,5.6 L11.2,9.6 L10.4,8.8 L13.0,6.2 L1.6,6.2 L1.6,5.0 L13.0,5.0 L10.4,2.4 L11.2,1.6 Z M4.8,6.4 L5.6,7.2 L3.0,9.8 L14.4,9.8 L14.4,11.0 L3.0,11.0 L5.6,13.6 L4.8,14.4 L0.8,10.4 L4.8,6.4 Z"
      />
    </svg>
  `,
  search: classes => html`
    <svg
      class=${classes}
      width="32"
      height="32"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M32.056 30.171l-1.886 1.886-8.413-8.413c-2.153 1.74-4.894 2.781-7.879 2.781-6.928 0-12.545-5.615-12.545-12.545 0-6.928 5.617-12.545 12.545-12.545 6.93 0 12.545 5.617 12.545 12.545 0 2.985-1.042 5.725-2.781 7.879l8.413 8.413zM13.879 4c-5.456 0-9.879 4.423-9.879 9.879 0 5.457 4.422 9.879 9.879 9.879 5.457 0 9.879-4.421 9.879-9.879 0-5.456-4.422-9.879-9.879-9.879z"
      />
    </svg>
  `,
  'image-add': classes => html`
    <svg
      class=${classes}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M11 2.75H4A1.25 1.25 0 0 0 2.75 4v12A1.25 1.25 0 0 0 4 17.25h12A1.25 1.25 0 0 0 17.25 16V9"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
      <path
        d="M2.75 14l4-4 3.5 3.5 2-2 5 5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      <circle cx="12.5" cy="7.5" r="1.25" fill="currentColor" />
      <path
        d="M16 1.5v5M13.5 4h5"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
    </svg>
  `,
};

// `classes` carries the class list the old <img> had, so component CSS keeps
// working after the swap.
export function icon(name, classes = '') {
  const template = ICONS[name];

  return template ? template(classes) : html``;
}
